export type TProfileSettingsTabKey =
  | "general"
  | "preferences"
  | "notifications"
  | "security"
  | "work_hours"
  | "skills"
  | "connected_apps";

export interface IProfileSettingsTab {
  key: TProfileSettingsTabKey;
  labelKey: string;
}

export const PROFILE_SETTINGS_TABS: IProfileSettingsTab[] = [
  { key: "general", labelKey: "profile_settings.tabs.general" },
  { key: "preferences", labelKey: "profile_settings.tabs.preferences" },
  { key: "notifications", labelKey: "profile_settings.tabs.notifications" },
  { key: "security", labelKey: "profile_settings.tabs.security" },
  { key: "work_hours", labelKey: "profile_settings.tabs.work_hours" },
  { key: "skills", labelKey: "profile_settings.tabs.skills" },
  { key: "connected_apps", labelKey: "profile_settings.tabs.connected_apps" },
];

/** `/profile/<segment>` → the tab that deep link opens.
 *
 *  Lives here rather than in `routes/redirects/profile-redirect.tsx` because
 *  **`app/routes.ts` has to register a static route per segment and cannot be allowed
 *  to disagree with this map** (NG-1134). React Router ranks a matched branch by
 *  score, not by declaration order, and a splat is penalised: `profile/*` scores 10
 *  while `:workspaceSlug/notifications` scores 15, so `/profile/notifications` was
 *  matched by the *workspace* notifications route with `workspaceSlug="profile"` — the
 *  wrapper could not resolve that slug and bounced the person to their last workspace,
 *  so the profile-settings modal never opened. An explicit `profile/notifications`
 *  scores 22 and wins.
 *
 *  `notifications` was the only one of the six that collided, which is why the other
 *  five deep links worked and this one silently did not: it is not a property of the
 *  tab, it is a property of whichever segments `/:workspaceSlug/...` happens to own
 *  today. Registering all six statically means the next `/:workspaceSlug/<segment>`
 *  route cannot take one over, and `profile-redirect.route.test.ts` fails if a segment
 *  is added here without its route. */
export const PROFILE_TAB_BY_SEGMENT: Record<string, TProfileSettingsTabKey> = {
  preferences: "preferences",
  notifications: "notifications",
  security: "security",
  "work-hours": "work_hours",
  skills: "skills",
  "connected-apps": "connected_apps",
};

/** The `/profile/<segment>` paths `app/routes.ts` registers statically. */
export const PROFILE_ROUTE_SEGMENTS: string[] = Object.keys(PROFILE_TAB_BY_SEGMENT);
